/**
 * Formatting Utilities
 *
 * Centralized formatting functions for dates, prices, currencies, etc.
 * Eliminates duplicated formatting logic across components
 */

/**
 * Format a Firestore timestamp or Date object to a localized date string
 *
 * @param {Object|Date|string|number} timestamp - Firestore timestamp, Date, or ISO string
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string or "N/A" if invalid
 *
 * @example
 * formatDate(firestoreTimestamp) // "12/19/2025"
 * formatDate(new Date(), { month: 'long', day: 'numeric', year: 'numeric' }) // "December 19, 2025"
 */
export function formatDate(timestamp, options = {}) {
  if (!timestamp) return "N/A";

  try {
    // Handle Firestore timestamp
    let date;
    if (timestamp.toDate) {
      // Proper Firestore Timestamp object
      date = timestamp.toDate();
    } else if (timestamp._seconds) {
      // Raw Firestore timestamp format {_seconds, _nanoseconds}
      date = new Date(timestamp._seconds * 1000);
    } else {
      // Try direct conversion
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      return "N/A";
    }

    const defaultOptions = {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      ...options,
    };

    return date.toLocaleDateString("en-GB", defaultOptions);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "N/A";
  }
}

/**
 * Format a Firestore timestamp or Date object to a localized date and time string
 *
 * @param {Object|Date|string|number} timestamp - Firestore timestamp, Date, or ISO string
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted datetime string or "N/A" if invalid
 *
 * @example
 * formatDateTime(firestoreTimestamp) // "12/19/2025, 2:30 PM"
 */
export function formatDateTime(timestamp, options = {}) {
  if (!timestamp) return "N/A";

  try {
    let date;
    if (timestamp.toDate) {
      // Proper Firestore Timestamp object
      date = timestamp.toDate();
    } else if (timestamp._seconds) {
      // Raw Firestore timestamp format {_seconds, _nanoseconds}
      date = new Date(timestamp._seconds * 1000);
    } else {
      // Try direct conversion
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      return "N/A";
    }

    const defaultOptions = {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      ...options,
    };

    return date.toLocaleDateString("en-GB", defaultOptions);
  } catch (error) {
    console.error("Error formatting datetime:", error);
    return "N/A";
  }
}

/**
 * Format a number as a price with currency symbol
 *
 * @param {number|string} price - Price value
 * @param {string} currency - Currency symbol (default: '£')
 * @param {Object} options - Formatting options
 * @returns {string} Formatted price string
 *
 * @example
 * formatPrice(250000) // "£250,000"
 * formatPrice(1500.50) // "£1,501"
 * formatPrice(250000, '$') // "$250,000"
 */
export function formatPrice(price, currency = "£", options = {}) {
  if (!price && price !== 0) return "N/A";

  try {
    // If already formatted with currency symbol, return as-is
    if (typeof price === "string" && price.includes(currency)) {
      return price;
    }

    const numPrice = typeof price === "string" ? parseFloat(price) : price;

    if (isNaN(numPrice)) {
      return "N/A";
    }

    const { decimals = 0, locale = "en-GB" } = options;

    const formatted = numPrice.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    return `${currency}${formatted}`;
  } catch (error) {
    console.error("Error formatting price:", error);
    return "N/A";
  }
}

/**
 * Format a number with thousand separators
 *
 * @param {number} num - Number to format
 * @param {string} locale - Locale for formatting (default: 'en-GB')
 * @returns {string} Formatted number string
 *
 * @example
 * formatNumber(1234567) // "1,234,567"
 */
export function formatNumber(num, locale = "en-GB") {
  if (num === null || num === undefined || isNaN(num)) return "0";
  return num.toLocaleString(locale);
}

/**
 * Format a phone number
 *
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 *
 * @example
 * formatPhone("01234567890") // "01234 567 890"
 * formatPhone("+441234567890") // "+44 1234 567 890"
 */
export function formatPhone(phone) {
  if (!phone) return "N/A";

  // Remove all non-numeric characters except +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // UK mobile format: 07XXX XXX XXX
  if (cleaned.match(/^07\d{9}$/)) {
    return cleaned.replace(/(\d{5})(\d{3})(\d{3})/, "$1 $2 $3");
  }

  // UK landline format: 0XXXX XXX XXX
  if (cleaned.match(/^0\d{10}$/)) {
    return cleaned.replace(/(\d{5})(\d{3})(\d{3})/, "$1 $2 $3");
  }

  // International format: +XX XXXX XXX XXX
  if (cleaned.startsWith("+")) {
    return cleaned.replace(/(\+\d{2})(\d{4})(\d{3})(\d{3})/, "$1 $2 $3 $4");
  }

  return phone;
}

/**
 * Format a postcode
 *
 * @param {string} postcode - UK postcode
 * @returns {string} Formatted postcode
 *
 * @example
 * formatPostcode("sw1a1aa") // "SW1A 1AA"
 */
export function formatPostcode(postcode) {
  if (!postcode) return "N/A";

  const cleaned = postcode.replace(/\s/g, "").toUpperCase();

  // UK postcode format: XX(X) X(X)XX
  const match = cleaned.match(/^([A-Z]{1,2}\d{1,2}[A-Z]?)(\d[A-Z]{2})$/);
  if (match) {
    return `${match[1]} ${match[2]}`;
  }

  return postcode.toUpperCase();
}

/**
 * Format file size in bytes to human-readable format
 *
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted file size
 *
 * @example
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(1536000) // "1.46 MB"
 */
export function formatFileSize(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";
  if (!bytes) return "N/A";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

/**
 * Truncate text to a maximum length with ellipsis
 *
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length (default: 50)
 * @param {string} ellipsis - Ellipsis string (default: '...')
 * @returns {string} Truncated text
 *
 * @example
 * truncate("This is a very long text", 10) // "This is a..."
 */
export function truncate(text, maxLength = 50, ellipsis = "...") {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - ellipsis.length) + ellipsis;
}

/**
 * Format a percentage
 *
 * @param {number} value - Decimal value (0.5 = 50%)
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted percentage
 *
 * @example
 * formatPercent(0.75) // "75%"
 * formatPercent(0.3333, 2) // "33.33%"
 */
export function formatPercent(value, decimals = 0) {
  if (value === null || value === undefined || isNaN(value)) return "0%";
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 *
 * @param {Date|string|number} date - Date to format
 * @returns {string} Relative time string
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 3600000)) // "1 hour ago"
 */
export function formatRelativeTime(date) {
  if (!date) return "N/A";

  try {
    const timestamp = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const diffMs = now - timestamp;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return "just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;

    return formatDate(timestamp);
  } catch (error) {
    console.error("Error formatting relative time:", error);
    return "N/A";
  }
}
